import { Stack } from '@mui/material';
import _ from 'lodash';
import { getBalancesReport, getReportByType } from 'services/reports.service';
import { getAllCompanies } from 'services/companies.service';
import { useState, Fragment, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { FormButton } from '../theme/elements/Buttons';
import { CenteredNarrowContent } from '../theme/elements/Content';
import { Page } from '../theme/elements/Page';
import { FormHeader2 } from '../theme/elements/Headers';
import { setListOfInvalids } from 'components/FormCreator/validation';
import { FormCard } from '../theme/elements/Forms';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { ErrorText } from 'components/ErrorText';
import { fieldsGroupType, fieldType, Ifield, invalidFields, validationTypes } from 'components/FormCreator/types';
import {
	formatDate,
	reportFieldsValidations,
	ReportInputFields,
	reportTypes,
	formatDateToLocaleString,
} from 'constants/reports.constants';
import { isLoading, getReportDates, setReportDates, getError } from 'state/reducers/reports.reducers';
import { FormCreator } from 'components/FormCreator/index';
import { permissionEnums } from 'constants/permission.constants';
import { hasPermission } from './AuthorizationComponent';
import { ButtonComponent } from './ButtonComponent';
import { defineds } from 'constants/ui.constants';
import { getUserDetails } from 'state/reducers/user.reducers';

export const Reports = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const loading = useAppSelector(isLoading);
	const userDetails = useAppSelector(getUserDetails);
	const [companies, setCompanies] = useState([]);
	const [companiesNames, setCompaniesNames] = useState([]);
	const [selectedCompanyName, setSelectedCompanyName] = useState('');
	const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>();
	const [companyStartDate, setCompanyStartDate] = useState('');
	const [disabledSubmit, setDisabledSubmit] = useState(true);
	const [invalidFields, setInvalidFields] = useState<invalidFields>({});
	const [reportTypesByPermissions, setReportTypesByPermissions] = useState<Array<any>>([]);
	const [reportType, setReportType] = useState('');
	const error = useAppSelector(getError);
	const title = t('report.title');
	const reportTypeLabel = t('report.reportType');
	const companyNameLabel = t('report.companyName');
	const dateRangeLabel = t('report.reportDate');
	const yesterday = new Date();
	yesterday.setDate(yesterday.getDate() - 1);
	const dateRange = useAppSelector(getReportDates);

	// Reports that require company selection
	const COMPANY_REQUIRED_REPORTS = ['Balances', 'Refunds', 'AccountActivities'];

	// Determine if we're still loading data (permissions + companies)
	const isDataLoading = useMemo(() => {
		// Loading if: no user details, no permissions, or no companies loaded yet
		const permissionsLoaded = userDetails?.permissions?.permissions?.length > 0;
		const companiesLoaded = companies.length > 0;
		const loading = !permissionsLoaded || !companiesLoaded;
		return loading;
	}, [userDetails?.permissions?.permissions, companies.length]);

	const dates =
		reportType == 'Balances'
			? formatDate(dateRange.startDate)
			: reportType === 'AccountActivities' && (!dateRange.startDate || !dateRange.endDate)
			? ''
			: `${formatDate(dateRange.startDate)} עד ${formatDate(dateRange.endDate)}`;
	const companiesReportPermission = hasPermission([permissionEnums.CLIENTS_ACTIVITY_COMPANIES_REPORT]);
	const beneficiariesReportPermission = hasPermission([permissionEnums.CLIENTS_ACTIVITY_BENEFICIARIES_REPORT]);
	const balancesReportPermission = hasPermission([permissionEnums.CLIENTS_ACTIVITY_BALANCES_REPORT]);
	const transactionsReportPermission = hasPermission([permissionEnums.CLIENTS_ACTIVITY_TRANSACTIONS_REPORT]);
	const refundsReportPermission = hasPermission([permissionEnums.CLIENTS_ACTIVITY_REFUNDS_REPORT]);
	const paymentFeesReportPermission = hasPermission([permissionEnums.CLIENTS_ACTIVITY_PAYMENT_FEES_REPORT]);
	const conversionFeesReportPermission = hasPermission([permissionEnums.CLIENTS_ACTIVITY_CONVERSION_FEES_REPORT]);
	const accountActivitiesReportPermission = hasPermission([
		permissionEnums.CLIENTS_ACTIVITY_ACCOUNT_ACTIVITIES_REPORT,
	]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		if (_.has(reportFieldsValidations, name)) {
			setInvalidFields(setListOfInvalids(invalidFields, reportFieldsValidations as any, name, value));
		}
		// Reports that require company selection should disable submit until company is selected
		const requiresCompany = COMPANY_REQUIRED_REPORTS.includes(value);
		!value || requiresCompany ? setDisabledSubmit(true) : setDisabledSubmit(false);
		reportTypes.some((obj) => obj.value == value)
			? (setReportType(value), setSelectedCompanyName(''))
			: setSelectedCompanyName(value);
		if (!value && name === 'ReportType') {
			setReportType('');
		}
	};

	const handleReport = () => {
		const reportStartDate = dateRange.startDate ? formatDateToLocaleString(dateRange.startDate) : null;
		const reportEndDate = dateRange.endDate ? formatDateToLocaleString(dateRange.endDate) : null;
		if (reportType == 'Balances') {
			dispatch(getBalancesReport(reportType, reportStartDate, reportEndDate, selectedCompanyId));
		} else {
			dispatch(getReportByType(reportType, reportStartDate, reportEndDate, selectedCompanyId));
		}
		setSelectedCompanyName('');
		if (COMPANY_REQUIRED_REPORTS.includes(reportType)) {
			setDisabledSubmit(true);
		}
	};

	useEffect(() => {
		// Only proceed if permissions are loaded
		if (!userDetails?.permissions?.permissions) {
			return;
		}

		const fetchCompanies = async () => {
			try {
				const fetchedCompanies = await dispatch(getAllCompanies());
				const names = fetchedCompanies.map((item: any) => {
					console.log(item);
					return { value: `${item.name}`, label: `${item.id}_${item.name}` };
				});
				setCompanies(fetchedCompanies);

				if (reportType === 'Refunds') {
					names.unshift({ value: 'all', label: t('reports.refund.all_companies') });
				}
				setCompaniesNames(names);
			} catch (error) {
				console.error('Error fetching companies:', error);
			}
		};
		fetchCompanies();

		const permissions = [
			transactionsReportPermission,
			balancesReportPermission,
			beneficiariesReportPermission,
			beneficiariesReportPermission,
			companiesReportPermission,
			refundsReportPermission,
			paymentFeesReportPermission,
			conversionFeesReportPermission,
			accountActivitiesReportPermission,
		];
		const reportTypesByPermission = reportTypes.map((item: any, index: number) => ({
			...item,
			isPermitted: permissions[index],
		}));
		const filteredReportTypesByPermission = reportTypesByPermission.filter((item: any) => item.isPermitted);
		setReportTypesByPermissions(filteredReportTypesByPermission);
	}, [reportType, userDetails?.permissions?.permissions]);

	useEffect(() => {
		if (selectedCompanyName) {
			if (selectedCompanyName === 'all') {
				setSelectedCompanyId(null);
			} else {
				const selectedCompanyIdx = companies.findIndex((item: any) => item.name === selectedCompanyName);
				const startDate: string = companies[selectedCompanyIdx]['created_at'];
				const companyId: number = companies[selectedCompanyIdx]['id'];
				setSelectedCompanyId(companyId);
				setCompanyStartDate(startDate.substring(0, 10));
				// Enable submit for company-requiring reports when company is selected
				if (COMPANY_REQUIRED_REPORTS.includes(reportType)) {
					setDisabledSubmit(false);
				}
			}
		}
		if (reportType && reportType === 'Balances') {
			dispatch(setReportDates({ startDate: yesterday, endDate: yesterday }));
		}
		if (reportType && reportType !== 'Balances') {
			if (reportType === 'AccountActivities') {
				// For AccountActivities, clear the dates to indicate no selection
				dispatch(setReportDates({ startDate: null, endDate: null }));
			} else {
				dispatch(setReportDates({ startDate: defineds.startOfMonth, endDate: new Date() }));
			}
		}
	}, [selectedCompanyName, companyStartDate, reportType]);

	const fields = [
		{
			type: fieldType.select,
			validation: [validationTypes.requierd],
			props: {
				required: true,
				name: ReportInputFields.REPORT_TYPE,
				label: reportTypeLabel,
				placeholder: reportTypeLabel,
				value: reportType,
				options: _.values(reportTypesByPermissions),
				disabled: loading,
			},
		},
	];

	const companyFields = {
		type: fieldType.select,
		validation: [validationTypes.requierd],
		props: {
			required: true,
			name: ReportInputFields.COMPANY_NAME,
			label: companyNameLabel,
			placeholder: companyNameLabel,
			value: selectedCompanyName,
			options: _.values(companiesNames),
			disabled: loading,
		},
	};

	const dateRangeField: Ifield = {
		type: fieldType.dateRangePicker,
		validation: reportType === 'AccountActivities' ? [] : [validationTypes.requierd],
		months: reportType === 'Balances' ? 1 : 2,
		props: {
			required: reportType !== 'AccountActivities',
			name: ReportInputFields.DATE_RANGE,
			label: reportType === 'Balances' ? t('report.reportSingleDate') : dateRangeLabel,
			placeholder: dateRangeLabel,
			value: dates,
			options: reportType === 'Balances' && selectedCompanyName ? companyStartDate : undefined,
			disabled: loading || (reportType === 'Balances' && !selectedCompanyName),
		},
	};

	// Determine which fields to show based on report type
	const getFormFields = () => {
		if (COMPANY_REQUIRED_REPORTS.includes(reportType)) {
			return [...fields, companyFields, dateRangeField];
		}
		return [...fields, dateRangeField];
	};

	return (
		<Page title={title} loading={isDataLoading}>
			<Fragment>
				<Stack spacing={1} sx={{ position: 'relative', height: '100%' }}>
					<CenteredNarrowContent>
						<FormCard>
							<FormHeader2 title={title} />
							<FormCreator
								initialValidation={invalidFields}
								name="report"
								handleChange={handleChange}
								fieldsGroups={[
									{
										type: fieldsGroupType.col,
										fields: getFormFields(),
									},
								]}
							/>
						</FormCard>
						<FormButton sx={{ marginTop: '32px', display: 'flex', justifyContent: 'center' }}>
							<ButtonComponent
								disabled={disabledSubmit || loading}
								handleClick={handleReport}
								text={t('report.reportDownload')}
								styleObj={{ width: '70%' }}
							/>
						</FormButton>
						{error && <ErrorText error={error} />}
					</CenteredNarrowContent>
				</Stack>
			</Fragment>
		</Page>
	);
};
